import { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { getFrontlineUser } from '@/auth/frontlineSession';
import { getInitials, toNaturalName } from '@/utils/strings';
import { getCurrentUserProfile } from '@/api/client';
import type { AdminPermissions, OuScope, UserModules } from '@/api/types';
import { hasOuScopedAdminRole, hasUnscopedRecognitionRole } from '@/auth/roleLabels';
import { usePlatform } from '@/hooks/usePlatform';

export type UserRole =
  | 'Employee'
  | 'AdminOU'
  | 'AdminGlobal'
  | 'AdminPulso'
  | 'AdminBrillamos'
  | 'AdminOUBrillamos'
  // Condición CALCULADA Manager (organigrama de Entra). No es un rol asignable: se declara aquí
  // porque las vistas del Programa de Reconocimiento la consultan igual que a un rol.
  | 'Manager';

/** Catálogo único de roles conocidos por el frontend: cualquier valor fuera de esta lista que
 *  llegue del backend se descarta. Se exporta para que no haya copias divergentes. */
export const VALID_ROLES: readonly UserRole[] = [
  'Employee', 'AdminOU', 'AdminGlobal', 'AdminPulso', 'AdminBrillamos', 'AdminOUBrillamos',
];

const NO_PERMISSIONS: AdminPermissions = {
  canCreateSurveys: false,
  canViewAnalytics: false,
  canManageUsers: false,
  canApproveSurveys: false,
};

/** El admin central no tiene restricción organizacional: ve reportes de todas las OUs. */
const UNRESTRICTED_OU_SCOPE: OuScope = { rootOuIds: [], allOusAccess: true };

const NO_OU_SCOPE: OuScope = { rootOuIds: [], allOusAccess: false };

const ALL_PERMISSIONS: AdminPermissions = {
  canCreateSurveys: true,
  canViewAnalytics: true,
  canManageUsers: true,
  canApproveSurveys: true,
};

/**
 * Respaldo mientras el perfil no se ha resuelto (o si falla la carga): ningún módulo.
 *
 * 🔴 Bug real (fila 112, reportado 2026-09-04): antes era `canViewPulso: true` — durante la
 * ventana async entre el mount y que `getCurrentUserProfile` resuelva, cualquier usuario veía
 * el menú de Pulso un instante, que desaparecía en cuanto llegaban los módulos reales del
 * backend. El supuesto ("Pulso es la fase liberada") quedó obsoleto: el negocio ya confirmó en
 * UserAdminService-api (CHANGELOG, fix de `CanViewPulso` en altas nuevas) que debe quedar en
 * `false` para TODOS mientras Pulso no salga de piloto — "en PRD no debe haber NADA de pulso
 * todavía" es la regla vigente, no la que asumía esta constante.
 */
const FALLBACK_MODULES: UserModules = { canViewPulso: false, canViewBrillamos: false };

/** Identidad organizacional del directorio (puesto, área y OU). */
interface OrgIdentity {
  jobTitle?: string;
  ouId?: string;
  ouName?: string;
  department?: string;
  photoUrl?: string | null;
}

/** Sin perfil resuelto no se inventa ninguno de estos campos: la UI omite el chip vacío. */
const NO_ORG_IDENTITY: OrgIdentity = {};

export interface CurrentUser {
  id: string;
  displayName: string;
  email: string;
  tenantId: string;
  roles: UserRole[];
  initials: string;
  /**
   * Rol de mayor privilegio entre los asignados. Sirve ÚNICAMENTE para elegir la etiqueta que se
   * muestra en la UI; ya no decide ningún acceso — eso lo resuelve `roles` (lista completa)
   * junto con `permissions` y `modules` en `canAccessView`/`accessMatrix`.
   */
  primaryRole: UserRole;
  preferredLanguage?: string;
  givenName?: string;
  /**
   * Identidad organizacional resuelta desde el directorio (UserAdminService → `UserDto`, que a su
   * vez la sincroniza de Graph). La consume la tarjeta del Perfil: `jobTitle` pinta `.rp2-puesto`
   * y `department`/`ouName` los dos `.rp2-meta-chip`. `ouId` es la clave que viaja a las APIs
   * (campaña activa, alcance GDPR, reportería); `ouName` es solo etiqueta. No confundirlas.
   */
  jobTitle?: string;
  ouId?: string;
  ouName?: string;
  department?: string;
  /**
   * Foto de perfil de AD/Entra ID, sincronizada por UserAdminService desde Graph. PENDIENTE de
   * backend (ver PENDIENTES.md): mientras el DTO no traiga `photoUrl`, queda `undefined` y la
   * tarjeta del Perfil cae al avatar de iniciales de siempre.
   */
  photoUrl?: string | null;
  /**
   * Permisos granulares que el AdminGlobal activa en la pantalla de roles. El AdminGlobal
   * siempre los tiene todos; el AdminOU solo los que tenga marcados; el Employee ninguno.
   */
  permissions: AdminPermissions;
  /**
   * Permisos canónicos efectivos del módulo Reconocimiento (PermissionKeys.Order), o null mientras
   * se resuelve el perfil. Gatea las 12 secciones del Admin de Fundamentales — ver accessMatrix.ts.
   */
  recognitionPermissions: string[] | null;
  /**
   * Alcance organizacional para los reportes: el AdminGlobal no tiene restricción; el AdminOU
   * solo ve a las personas de sus OUs configuradas y de los niveles inferiores de esas ramas.
   */
  ouScope: OuScope;
  /**
   * Alcance organizacional sobre Reconocimiento/Brillamos (campañas, puntos): un AdminOUBrillamos
   * solo ve sus OUs delegadas y las de niveles inferiores. Distinto de `ouScope`, que es el alcance
   * de Pulso — un AdminOUBrillamos no administra Pulso, así que las vistas de Brillamos deben leer
   * este campo (fix 2026-08-12; antes `AdminCampaigns`/`AdminPoints` leían `ouScope`, que para un
   * admin sin rol de Pulso venía vacío).
   */
  recognitionOuScope: OuScope;
  /** Módulos del producto habilitados para el usuario (Pulso / Brillamos). */
  modules: UserModules;
  /**
   * true cuando el perfil de la BD YA resolvió (éxito o fallo): `modules`/`permissions`/
   * `recognitionPermissions` reflejan el valor REAL y no el fallback de carga. Úsalo para no decidir
   * ruteo con los módulos a medio cargar — el valor inicial de `modules` es "solo Pulso" (RF-011).
   */
  profileResolved: boolean;
  /**
   * Espejo de CurrentUserProfile.profileStatus, con un cuarto valor ('pending') para la ventana
   * antes de que el fetch resuelva — CurrentUserProfile nunca representa ese estado porque solo
   * existe una vez que la promesa se resuelve. Un router-level gate usa este campo para decidir
   * si alguien autenticado por AD pero sin ficha en Mongo ('not-found') solo puede ver la página
   * informativa de Fundamentales.
   */
  profileStatus: 'resolved' | 'not-found' | 'error' | 'pending' | 'pending-hire-date';
}

/** Precedencia de la etiqueta de rol, de mayor a menor privilegio. */
const ROLE_PRECEDENCE: readonly UserRole[] = [
  'AdminGlobal', 'AdminPulso', 'AdminBrillamos', 'AdminOU', 'AdminOUBrillamos', 'Employee',
];

/**
 * Rol de mayor privilegio entre los asignados. Solo determina la etiqueta que se muestra: la
 * autorización usa la lista completa de roles.
 */
function resolvePrimaryRole(assignedRoles: readonly UserRole[]): UserRole {
  return ROLE_PRECEDENCE.find(role => assignedRoles.includes(role)) ?? 'Employee';
}

/**
 * Usuario actual. La IDENTIDAD viene de MSAL (Azure AD); los ROLES y el preferredLanguage
 * vienen de la BD de la app (UserAdminService → ResolveByEmail), NO del token. Azure AD solo
 * autentica; los roles y el idioma se gestionan en la aplicación. Fail-soft: si no se resuelve
 * el perfil → roles Employee, preferredLanguage undefined.
 */
export function useCurrentUser(): CurrentUser | null {
  const { instance, accounts } = useMsal();
  const { teamsContext } = usePlatform();
  // Prioriza la cuenta activa de ESTA pestaña (setActiveAccount en AuthProvider); accounts[0] solo
  // como respaldo. Con varias cuentas MSAL cacheadas en el mismo localStorage del navegador (p. ej.
  // una sesión normal y otra en incógnito), accounts[0] no necesariamente es la cuenta activa aquí.
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;
  // RF-002 — Usuario Frontline (sin Azure AD ni Teams): la identidad viene de la sesión propia
  // (frontlineSession). Los roles/OU los resuelve igual el efecto de abajo por correo (o cae a
  // Employee), y el JWT propio autoriza las llamadas API. Los corporativos NO pasan por aquí.
  const frontline = !account && !teamsContext ? getFrontlineUser() : null;
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [preferredLanguage, setPreferredLanguage] = useState<string | undefined>(undefined);
  const [dbGivenName, setDbGivenName] = useState<string | undefined>(undefined);
  const [grantedPermissions, setGrantedPermissions] = useState<AdminPermissions>(NO_PERMISSIONS);
  const [grantedOuScope, setGrantedOuScope] = useState<OuScope>(NO_OU_SCOPE);
  const [grantedRecognitionOuScope, setGrantedRecognitionOuScope] = useState<OuScope>(NO_OU_SCOPE);
  const [grantedModules, setGrantedModules] = useState<UserModules>(FALLBACK_MODULES);
  const [orgIdentity, setOrgIdentity] = useState<OrgIdentity>(NO_ORG_IDENTITY);
  const [recognitionPermissions, setRecognitionPermissions] = useState<string[] | null>(null);
  // Identificador de la ficha en el padrón, resuelto por correo. Ver la nota de `id` más abajo.
  const [fichaId, setFichaId] = useState<string | undefined>(undefined);
  // false hasta que el fetch del perfil (éxito o fallo) escribe los valores REALES. Evita decidir
  // ruteo (RF-011) con `modules` en su fallback "solo Pulso" durante la carga.
  const [profileResolved, setProfileResolved] = useState(false);
  const [profileStatus, setProfileStatus] =
    useState<'resolved' | 'not-found' | 'error' | 'pending' | 'pending-hire-date'>('pending');

  // Identidad: dentro de Teams viene del contexto de Teams (SSO); en navegador, de MSAL.
  // Se usan primitivos para mantener estables las dependencias de los hooks.
  // 🔴 La ficha del padrón manda sobre el identificador del token.
  //
  // `teamsContext.userId` y `account.localAccountId` son el `oid` de Entra, que coincide con el
  // identificador de la ficha SÓLO cuando ésta nació del directorio corporativo. Para el padrón de
  // SuccessFactors y del Excel de Europa/EE. UU. no coincide.
  //
  // El backend firma los reconocimientos con el identificador de la FICHA (ticket 222). Si la web
  // siguiera identificando por el `oid`, esa gente dejaría de reconocerse como dueña de lo suyo:
  // no podría borrar ni editar sus publicaciones (`PostItDetailModal`) y su lista de «Enviados»
  // saldría vacía (los filtros `emisorId` de BrillamosView y PerfilView).
  //
  // No hay ventana de arranque que cubrir: `AppRouter` no monta el árbol protegido mientras
  // `profileStatus` es 'pending', así que cuando estos consumidores existen la ficha ya resolvió.
  // Si aun así no resolviera —directorio caído, o sin correo en el token— se cae al `oid`, que es
  // el comportamiento de siempre.
  //
  // `frontline.id` va al final y no hace falta anteponerlo: el login por contraseña ya emite el
  // identificador de la ficha como `oid`, así que para esa gente los dos valores son el mismo.
  const id = fichaId || teamsContext?.userId || account?.localAccountId || frontline?.id || '';
  const email = teamsContext?.userPrincipalName || account?.username || frontline?.email || '';
  // El AD trae el displayName como "Apellidos Nombre". Para mostrar "Nombre Apellidos" SIN
  // modificar el AD, componemos desde los claims separados given_name (nombre) + family_name
  // (apellidos) cuando ambos vienen en el token. Si no, fallback al displayName de siempre.
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const azureGivenName = typeof claims.given_name === 'string' ? claims.given_name : '';
  // Precedencia del nombre de pila: Azure AD (claim del token, tiempo real) sobre el registrado
  // en la BD (UserAdminService → GivenName, sincronizado desde Graph). Si ninguno está presente,
  // el consumidor final cae a displayName.
  const givenName = azureGivenName || dbGivenName || '';
  const familyName = typeof claims.family_name === 'string' ? claims.family_name : '';
  const composedName = givenName && familyName ? `${givenName} ${familyName}` : '';
  // P3-4 — la rama de respaldo es la que traía el `displayName` CRUDO del AD
  // ("(Ext.) De la Cruz Morales, Ernesto"). `toNaturalName` es la regla única de la app; sobre un
  // nombre ya compuesto desde los claims no tiene efecto (no lleva marcador ni coma).
  const name = toNaturalName(composedName || teamsContext?.displayName || account?.name || (frontline?.displayName ?? undefined)) || email || 'Usuario';
  const tenantId = teamsContext?.tenantId || account?.tenantId || '';
  const hasIdentity = Boolean(id || email);

  useEffect(() => {
    let cancelled = false;
    // Nuevo email → se re-espera el perfil (los módulos vuelven a su fallback hasta que resuelva).
    setProfileResolved(false);
    setProfileStatus('pending');
    // Se limpia aquí, antes de las ramas, para que ninguna pueda dejar colgada la ficha de un
    // correo anterior. Mientras esté en 'pending' nadie lee `id`: el árbol protegido no se monta.
    setFichaId(undefined);
    if (!email) {
      setRoles([]);
      setPreferredLanguage(undefined);
      setDbGivenName(undefined);
      setGrantedPermissions(NO_PERMISSIONS);
      setGrantedOuScope(NO_OU_SCOPE);
      setGrantedRecognitionOuScope(NO_OU_SCOPE);
      setGrantedModules(FALLBACK_MODULES);
      setOrgIdentity(NO_ORG_IDENTITY);
      setRecognitionPermissions(null);
      // Con identidad (id) pero sin correo resoluble no hay forma de consultar el perfil — no es
      // un estado transitorio, es terminal. Tratarlo como 'not-found' (gatea) en vez de dejarlo en
      // 'pending' para siempre: es exactamente el perfil (guest/Teams sin UPN) que este gate debe
      // cubrir (hallazgo C2 de la revisión final, 2026-08-13). Sin id tampoco, currentUser ya sale
      // null (hasIdentity false) y este valor nunca se lee.
      setProfileStatus(hasIdentity ? 'not-found' : 'pending');
      // Con hasIdentity false (Teams/MSAL aún no resolvió), NO marcar resuelto: `currentUser`
      // ya es null en ese caso (ver el guard `if (!hasIdentity) return null` más abajo), así
      // que fijar 'resolved' aquí solo colaba datos fallback (Employee, sin permisos) hacia
      // guards que esperan a `profileResolved` antes de decidir (bug QA "Administración se
      // atora": condición de carrera confirmada en Teams, `useCurrentUser` se reinicia en
      // cada remount y `teamsContext` tarda un render extra en poblarse).
      setProfileResolved(hasIdentity);
      return;
    }
    getCurrentUserProfile(email)
      .then((profile) => {
        if (cancelled) return;
        const valid = profile.roles.filter((r): r is UserRole => VALID_ROLES.includes(r as UserRole));
        setRoles(valid.length > 0 ? valid : ['Employee']);
        setPreferredLanguage(profile.preferredLanguage);
        setDbGivenName(profile.givenName);
        setFichaId(profile.fichaId);
        setGrantedPermissions(profile.permissions);
        setGrantedOuScope(profile.ouScope);
        setGrantedRecognitionOuScope(profile.recognitionOuScope);
        setGrantedModules(profile.modules);
        setOrgIdentity({
          jobTitle: profile.jobTitle,
          ouId: profile.ouId,
          ouName: profile.ouName,
          department: profile.department,
          photoUrl: profile.photoUrl,
        });
        setRecognitionPermissions(profile.recognitionPermissions);
        setProfileStatus(profile.profileStatus);
        setProfileResolved(true);
      })
      .catch((error) => {
        if (import.meta.env.DEV) console.error('useCurrentUser: failed to load profile, defaulting to Employee', error);
        if (!cancelled) {
          setRoles(['Employee']);
          setPreferredLanguage(undefined);
          setDbGivenName(undefined);
          setGrantedPermissions(NO_PERMISSIONS);
          setGrantedOuScope(NO_OU_SCOPE);
      setGrantedRecognitionOuScope(NO_OU_SCOPE);
          setGrantedModules(FALLBACK_MODULES);
          setOrgIdentity(NO_ORG_IDENTITY);
          setRecognitionPermissions(null);
          setProfileStatus('error');
          // El perfil YA resolvió (aunque fallara): degradar a Employee/fallback es una decisión
          // final, no un estado de carga. Sin esto el ruteo se quedaría esperando para siempre.
          setProfileResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // hasIdentity SÍ debe estar en las deps (re-review del hallazgo C2, 2026-08-14): en Teams,
    // usePlatform arranca con teamsContext null y lo resuelve en un efecto propio, en un render
    // POSTERIOR al mount — así que `id` puede pasar de '' a un valor real sin que `email` cambie
    // nunca (Teams con userPrincipalName vacío). Con solo `[email]`, este efecto corría una única
    // vez con hasIdentity todavía false, fijaba 'pending' y jamás volvía a correr: profileStatus
    // se quedaba en 'pending' para siempre pese a que `currentUser` ya no era null (el useMemo de
    // más abajo sí recalcula, porque tiene sus propias deps). Con hasIdentity en las deps, el
    // efecto reacciona cuando Teams resuelve el id y puede fijar 'not-found'. Es seguro: hasIdentity
    // es `Boolean(id || email)`, así que si `email` ya es verdadero no puede "voltear" de nuevo
    // (ya es true) — no genera un refetch redundante en el camino normal (email presente).
  }, [email, hasIdentity]);

  return useMemo(() => {
    if (!hasIdentity) return null;
    const effectiveRoles: UserRole[] = roles.length > 0 ? roles : ['Employee'];
    // Los administradores sin ámbito de OU (General, Pulso, Brillamos) no dependen de los
    // permisos granulares ni de una rama concreta del organigrama.
    //
    // Un rol acotado a una OU manda sobre uno global: es el mismo criterio con el que la pantalla
    // de administración exige la OU (hasOuScopedAdminRole). Sin esta condición, conceder Admin
    // Brillamos —un módulo que hoy no tiene vistas propias— a un Admin OU Pulso le borraba en
    // silencio la restricción de su rama del organigrama y le daba los cuatro permisos granulares
    // que el Admin General pudo haberle negado a propósito.
    const isUnscopedAdmin = !hasOuScopedAdminRole(effectiveRoles)
      && effectiveRoles.some(
        role => role === 'AdminGlobal' || role === 'AdminPulso' || role === 'AdminBrillamos');
    // Bug UAT #114 — el alcance de RECONOCIMIENTO se decide con su propio predicado, no con el de
    // arriba. La regla "un rol acotado manda sobre uno global" existe para no borrarle a un Admin
    // OU Pulso la restricción de su rama en PULSO; aplicada a Brillamos hace lo contrario de lo
    // que se busca: deja SIN alcance a un Admin Reconocimiento que además tenga un rol acotado,
    // porque su `RecognitionScopeOuIds` está vacío por definición (`AppUser.SetRecognitionScope`).
    // Ver `hasUnscopedRecognitionRole` — espejo de `CampaignsController.RequireScope`.
    const isUnscopedRecognitionAdmin = hasUnscopedRecognitionRole(effectiveRoles);
    return {
      id,
      displayName: name,
      email,
      tenantId,
      roles: effectiveRoles,
      initials: getInitials(name),
      primaryRole: resolvePrimaryRole(effectiveRoles),
      preferredLanguage,
      givenName,
      jobTitle: orgIdentity.jobTitle,
      ouId: orgIdentity.ouId,
      ouName: orgIdentity.ouName,
      department: orgIdentity.department,
      photoUrl: orgIdentity.photoUrl,
      // Los admins sin ámbito de OU acceden a las secciones del panel sin depender de los
      // permisos granulares de la pantalla de roles.
      permissions: isUnscopedAdmin ? ALL_PERMISSIONS : grantedPermissions,
      ouScope: isUnscopedAdmin ? UNRESTRICTED_OU_SCOPE : grantedOuScope,
      recognitionOuScope: isUnscopedRecognitionAdmin ? UNRESTRICTED_OU_SCOPE : grantedRecognitionOuScope,
      modules: grantedModules,
      recognitionPermissions,
      profileResolved,
      profileStatus,
    };
  }, [hasIdentity, id, name, email, tenantId, roles, preferredLanguage, givenName, orgIdentity, grantedPermissions, grantedOuScope, grantedRecognitionOuScope, grantedModules, recognitionPermissions, profileResolved, profileStatus]);
}
